import { Vector3 } from 'three';

import type { Skeleton } from '../../skeleton/core';
import {
  isAnimationRootLikeName,
  isContainerLikeName,
  isLegLikeName,
  isPelvisLikeName,
  isPrimaryRootLikeName,
  isSecondaryRootLikeName,
  isSpineLikeName,
} from '../../schema/mapping';
import type { SkeletonSpatialData } from '../spatial';
import { CandidateInfo, type RecognizerConfig } from '../config';
import { scoreCandidate } from '../helpers';

function spatialRootScore(
  skel: Skeleton,
  boneIdx: number,
  spatialData: SkeletonSpatialData | null,
): number {
  if (spatialData === null) return 0.0;

  const bone = skel.bone(boneIdx);
  const e = bone.restWorld.elements;
  const px = e[12];
  const py = e[13];
  const pz = e[14];
  const distFromOrigin = Math.sqrt(px * px + py * py + pz * pz);
  if (distFromOrigin < 0.01) return 1.0;

  const vertPos = spatialData.getVerticalPosition(boneIdx);
  if (vertPos <= 0.05) return 1.0;
  if (vertPos <= 0.1) return 0.7;
  if (vertPos <= 0.2) return 0.35;
  return 0.1;
}

function pelvisActsAsAnimationRootHub(skel: Skeleton, pelvisIdx: number): boolean {
  const pelvis = skel.bone(pelvisIdx);
  if (!isAnimationRootLikeName(pelvis.name)) return false;

  const nonAuxChildren = pelvis.children.filter((childIdx) => !skel.bone(childIdx).auxiliary);
  let directLegChildren = 0;
  let hasSpineChild = false;

  for (const childIdx of nonAuxChildren) {
    const childName = skel.bone(childIdx).name.toLowerCase();
    if (isLegLikeName(childName) || isPelvisLikeName(childName)) {
      directLegChildren += 1;
    }
    if (isSpineLikeName(childName)) {
      hasSpineChild = true;
    }
  }

  return directLegChildren >= 2 && hasSpineChild;
}

export function detectRoot(
  skel: Skeleton,
  pelvisIdx: number,
  config: RecognizerConfig,
  spatialData: SkeletonSpatialData | null = null,
): [number, CandidateInfo] | null {
  const ancestors = skel
    .ancestors(pelvisIdx)
    .filter((a) => !skel.bone(a).auxiliary || a === skel.root);
  if (ancestors.length === 0) return null;

  let primaryRootIdx: number | null = null;
  let anyRootIdx: number | null = null;
  let spatialRootIdx: number | null = null;
  let bestSpatialScore = -1.0;

  for (let i = ancestors.length - 1; i >= 0; i--) {
    const ancestorIdx = ancestors[i];
    const bone = skel.bone(ancestorIdx);
    const container = isContainerLikeName(bone.name);

    if (spatialData !== null && !container) {
      const spScore = spatialRootScore(skel, ancestorIdx, spatialData);
      if (spScore >= bestSpatialScore) {
        bestSpatialScore = spScore;
        spatialRootIdx = ancestorIdx;
      }
    }

    if (isAnimationRootLikeName(bone.name)) {
      if (anyRootIdx === null) {
        anyRootIdx = ancestorIdx;
      }

      if (isPrimaryRootLikeName(bone.name)) {
        primaryRootIdx = ancestorIdx;
        break;
      }

      if (isSecondaryRootLikeName(bone.name)) {
        continue;
      }

      if (primaryRootIdx === null) {
        primaryRootIdx = ancestorIdx;
      }
    }
  }

  let rootIdx: number;
  if (primaryRootIdx !== null) {
    rootIdx = primaryRootIdx;
  } else if (anyRootIdx !== null) {
    rootIdx = anyRootIdx;
  } else if (spatialRootIdx !== null && bestSpatialScore >= 0.5) {
    rootIdx = spatialRootIdx;
  } else {
    return null;
  }

  if (pelvisActsAsAnimationRootHub(skel, pelvisIdx) && skel.bone(rootIdx).auxiliary) {
    return null;
  }

  const candidate = scoreCandidate(
    skel,
    rootIdx,
    'Root',
    null,
    null,
    1,
    null,
    null,
    null,
    spatialData,
  );
  return [rootIdx, candidate];
}
